import axios from "axios";

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;
export const FRIENDLY_CONNECTIVITY_ERROR =
  "We're having trouble connecting to the server right now. Please try again in a moment. If this continues, please contact the Elite Core technical team.";

export const RETRYING_CONNECTIVITY_MESSAGE = "This is taking longer than expected. We're retrying the request...";

const INTERNAL_ERROR_PATTERN = /(getaddrinfo|enotfound|econnreset|econnrefused|etimedout|eai_again|network error|sequelize.*connection|connection.*sequelize|rds\.amazonaws\.com|amazonaws\.com|database|db host|sqlstate|select .* from|insert into|update .* set|delete from|stack trace|\bat\s+\S+\s+\(|internal server error)/i;
const TRANSIENT_ERROR_PATTERN = /(getaddrinfo|enotfound|econnreset|econnrefused|etimedout|eai_again|network error|timeout|temporarily unavailable|rds\.amazonaws\.com|amazonaws\.com)/i;

const readApiMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return data?.message || data?.error || error?.message || "";
};

export function isInternalApiError(error) {
  const message = String(readApiMessage(error) || "");
  const status = Number(error?.response?.status);
  if (INTERNAL_ERROR_PATTERN.test(message)) return true;
  if (!error?.response && TRANSIENT_ERROR_PATTERN.test(String(error?.message || error?.code || ""))) return true;
  return status >= 500 && (!message || /internal server error/i.test(message));
}

export function isTransientApiError(error) {
  const message = String(readApiMessage(error) || "");
  const status = Number(error?.response?.status);
  if (!error?.response) return true;
  if ([502, 503, 504].includes(status)) return true;
  return TRANSIENT_ERROR_PATTERN.test([message, error?.message, error?.code].filter(Boolean).join(" "));
}

export function getFriendlyApiError(error, fallbackMessage = "Something went wrong. Please try again.") {
  const message = String(readApiMessage(error) || "").trim();
  if (isInternalApiError(error)) return FRIENDLY_CONNECTIVITY_ERROR;
  return message || fallbackMessage;
}

export async function retryTransientRequest(requestFn, options = {}) {
  const retries = Number.isInteger(options.retries) ? options.retries : 1;
  const delayMs = Number.isFinite(Number(options.delayMs)) ? Number(options.delayMs) : 450;
  let attempt = 0;
  let lastError;

  while (attempt <= retries) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isTransientApiError(error)) throw error;
      attempt += 1;
      options.onRetry?.(attempt, error);
      await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
    }
  }

  throw lastError;
}

function authConfig(extra = {}) {
  return {
    headers: {
      accessToken: localStorage.getItem("accessToken"),
    },
    ...extra,
  };
}

function withInstanceUniqueId(instanceUniqueId, extraParams = {}) {
  return {
    params: {
      instanceUniqueId,
      ...extraParams,
    },
  };
}

export async function getMarketingConnection(instanceUniqueId, provider) {
  return axios.get(
    `${BASE_URL}/crm/integrations/connections/${provider}`,
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
}

export async function saveMarketingConnection(instanceUniqueId, provider, payload) {
  return axios.put(
    `${BASE_URL}/crm/integrations/connections/${provider}`,
    {
      instanceUniqueId,
      ...payload,
    },
    authConfig()
  );
}

export async function testMarketingConnection(instanceUniqueId, provider) {
  return axios.post(
    `${BASE_URL}/crm/integrations/connections/${provider}/test`,
    { instanceUniqueId },
    authConfig()
  );
}

export async function syncMarketingConnection(instanceUniqueId, provider, options = {}) {
  return axios.post(
    `${BASE_URL}/crm/integrations/connections/${provider}/sync`,
    {
      instanceUniqueId,
      forceSync: options.forceSync === true,
    },
    authConfig()
  );
}

export async function getMarketingSyncLogs(instanceUniqueId, provider, options = {}) {
  return axios.get(
    `${BASE_URL}/crm/integrations/sync-logs`,
    authConfig(
      withInstanceUniqueId(instanceUniqueId, {
        provider,
        page: options.page || 1,
        rpp: options.rpp || 10,
      })
    )
  );
}

function normalizeMarketingProspectParams(instanceOrParams, provider, options = {}) {
  if (
    instanceOrParams &&
    typeof instanceOrParams === "object" &&
    !Array.isArray(instanceOrParams)
  ) {
    return {
      instanceUniqueId: instanceOrParams.instanceUniqueId,
      provider: instanceOrParams.provider,
      q: instanceOrParams.q,
      status: instanceOrParams.status,
      externalType: instanceOrParams.externalType,
      page: instanceOrParams.page || 1,
      rpp: instanceOrParams.rpp || 25,
    };
  }

  return {
    instanceUniqueId: instanceOrParams,
    provider,
    q: options.q,
    status: options.status,
    externalType: options.externalType,
    page: options.page || 1,
    rpp: options.rpp || 25,
  };
}

export async function getMarketingProspects(instanceOrParams, provider, options = {}) {
  const params = normalizeMarketingProspectParams(instanceOrParams, provider, options);

  return axios.get(
    `${BASE_URL}/crm/prospects`,
    authConfig(
      withInstanceUniqueId(params.instanceUniqueId, {
        provider: params.provider || undefined,
        page: params.page,
        rpp: params.rpp,
        q: params.q || undefined,
        status: params.status || undefined,
        externalType: params.externalType || undefined,
      })
    )
  );
}

export async function getMarketingProspectConversionPreview(instanceUniqueId, prospectId) {
  return axios.get(
    `${BASE_URL}/crm/prospects/${prospectId}/conversion-preview`,
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
}

export async function convertMarketingProspect(instanceUniqueId, prospectId, payload) {
  return axios.post(
    `${BASE_URL}/crm/prospects/${prospectId}/convert`,
    {
      ...payload,
      instanceUniqueId,
    },
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
}

export async function createProspectOpportunity(instanceUniqueId, prospectId, payload = {}) {
  return axios.post(
    `${BASE_URL}/crm/prospects/${prospectId}/create-opportunity`,
    {
      ...payload,
      instanceUniqueId,
    },
    authConfig()
  );
}

export async function fetchCrmPipelineBoard(params = {}) {
  return axios.get(
    `${BASE_URL}/crm/pipeline-board`,
    authConfig({ params })
  );
}

export async function fetchCrmNotificationCandidates(instanceUniqueId, options = {}) {
  const response = await axios.get(
    `${BASE_URL}/crm/notifications/candidates`,
    authConfig({
      params: {
        instanceUniqueId,
        limit: options.limit || 50,
        eventTypes: Array.isArray(options.eventTypes) && options.eventTypes.length
          ? options.eventTypes.join(",")
          : undefined,
      },
    })
  );
  return response.data;
}

export async function generateCrmNotifications(instanceUniqueId, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/notifications/generate`,
    {
      instanceUniqueId,
      eventTypes: payload.eventTypes,
      limit: payload.limit || 50,
    },
    authConfig()
  );
  return response.data;
}

export async function createCrmTag(instanceUniqueId, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/tags`,
    { ...payload, instanceUniqueId },
    authConfig()
  );
  return response.data;
}

export async function fetchCrmTags(instanceUniqueId, { includeInactive = false } = {}) {
  const response = await axios.get(
    `${BASE_URL}/crm/tags`,
    authConfig({ params: { instanceUniqueId, includeInactive } })
  );
  return response.data;
}

export async function updateCrmTag(instanceUniqueId, tagId, payload = {}) {
  const response = await axios.patch(
    `${BASE_URL}/crm/tags/${tagId}`,
    { ...payload, instanceUniqueId },
    authConfig()
  );
  return response.data;
}

export async function updateCrmCardTags(instanceUniqueId, cardType, id, tagIds = []) {
  const response = await axios.put(
    `${BASE_URL}/crm/cards/${cardType}/${id}/tags`,
    { instanceUniqueId, tagIds },
    authConfig()
  );
  return response.data;
}

export async function fetchCrmAccountabilityPolicies(instanceUniqueId) {
  const response = await axios.get(
    `${BASE_URL}/crm/accountability-policies`,
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
  return response.data;
}

export async function updateCrmAccountabilityPolicies(instanceUniqueId, policies = []) {
  const response = await axios.put(
    `${BASE_URL}/crm/accountability-policies`,
    {
      instanceUniqueId,
      policies,
    },
    authConfig()
  );
  return response.data;
}

export async function fetchCrmCardActivity(instanceUniqueId, cardType, id) {
  return axios.get(
    `${BASE_URL}/crm/cards/${cardType}/${id}/activity`,
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
}

export async function fetchCrmCardCommunications(instanceUniqueId, cardType, id) {
  return axios.get(
    `${BASE_URL}/crm/cards/${cardType}/${id}/communications`,
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
}

export async function createCrmCardCommunication(instanceUniqueId, cardType, id, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/cards/${cardType}/${id}/communications`,
    { ...payload, instanceUniqueId },
    authConfig()
  );
  return response.data;
}

export async function previewCrmEmailTemplate(instanceUniqueId, cardType, id, templateId) {
  const response = await axios.post(
    `${BASE_URL}/crm/cards/${cardType}/${id}/email-template-preview`,
    { instanceUniqueId, templateId },
    authConfig()
  );
  return response.data;
}

export async function saveCrmEmailTemplateDraft(instanceUniqueId, cardType, id, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/cards/${cardType}/${id}/email-template-drafts`,
    { ...payload, instanceUniqueId },
    authConfig()
  );
  return response.data;
}

export async function fetchCrmEmailAccounts(instanceUniqueId) {
  const response = await axios.get(`${BASE_URL}/crm/email-accounts`, authConfig(withInstanceUniqueId(instanceUniqueId)));
  return response.data;
}

export async function createCrmEmailAccount(instanceUniqueId, payload = {}) {
  const response = await axios.post(`${BASE_URL}/crm/email-accounts`, { ...payload, instanceUniqueId }, authConfig());
  return response.data;
}

export async function updateCrmEmailAccount(instanceUniqueId, id, payload = {}) {
  const response = await axios.put(`${BASE_URL}/crm/email-accounts/${id}`, { ...payload, instanceUniqueId }, authConfig());
  return response.data;
}

export async function authorizeMicrosoftCrmEmailAccount(instanceUniqueId, id) {
  const response = await axios.post(`${BASE_URL}/crm/email-accounts/${id}/microsoft/authorize`, { instanceUniqueId }, authConfig());
  return response.data;
}

export async function connectImapCrmEmailAccount(instanceUniqueId, id, payload = {}) {
  const response = await axios.post(`${BASE_URL}/crm/email-accounts/${id}/imap/connect`, { ...payload, instanceUniqueId }, authConfig());
  return response.data;
}

export async function disconnectCrmEmailAccount(instanceUniqueId, id) {
  const response = await axios.post(`${BASE_URL}/crm/email-accounts/${id}/disconnect`, { instanceUniqueId }, authConfig());
  return response.data;
}

export async function syncCrmEmailAccount(instanceUniqueId, id) {
  const response = await axios.post(`${BASE_URL}/crm/email-accounts/${id}/sync`, { instanceUniqueId }, authConfig());
  return response.data;
}

export async function fetchCrmEmailSyncStatus(instanceUniqueId, id) {
  const response = await axios.get(`${BASE_URL}/crm/email-accounts/${id}/sync-status`, authConfig(withInstanceUniqueId(instanceUniqueId)));
  return response.data;
}

export async function fetchCrmEmailRoutingSettings(instanceUniqueId) {
  const response = await axios.get(`${BASE_URL}/crm/email-routing-settings`, authConfig(withInstanceUniqueId(instanceUniqueId)));
  return response.data;
}

export async function updateCrmEmailRoutingSettings(instanceUniqueId, payload = {}) {
  const response = await axios.put(`${BASE_URL}/crm/email-routing-settings`, { ...payload, instanceUniqueId }, authConfig());
  return response.data;
}

export async function fetchCrmUnmappedCommunications(instanceUniqueId, options = {}) {
  const params = typeof options === "string" ? { status: options } : options;
  const response = await axios.get(
    `${BASE_URL}/crm/unmapped-communications`,
    authConfig(withInstanceUniqueId(instanceUniqueId, params))
  );
  return response.data;
}

export async function fetchCrmUnmappedCommunication(instanceUniqueId, id) {
  const response = await axios.get(
    `${BASE_URL}/crm/unmapped-communications/${id}`,
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
  return response.data;
}

export async function searchCrmCommunicationLinkTargets(instanceUniqueId, q, options = {}) {
  const response = await axios.get(
    `${BASE_URL}/crm/communication-link-targets`,
    authConfig(withInstanceUniqueId(instanceUniqueId, { q, limit: options.limit || 12 }))
  );
  return response.data;
}

export async function linkCrmUnmappedCommunication(instanceUniqueId, id, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/unmapped-communications/${id}/link`,
    { ...payload, instanceUniqueId },
    authConfig()
  );
  return response.data;
}

export async function dismissCrmUnmappedCommunication(instanceUniqueId, id, reason) {
  const response = await axios.post(
    `${BASE_URL}/crm/unmapped-communications/${id}/dismiss`,
    { instanceUniqueId, reason },
    authConfig()
  );
  return response.data;
}

export async function fetchCrmNextActivity(instanceUniqueId, cardType, id) {
  return axios.get(
    `${BASE_URL}/crm/cards/${cardType}/${id}/next-activity`,
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
}

export async function createCrmCardActivity(instanceUniqueId, cardType, id, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/cards/${cardType}/${id}/activities`,
    {
      ...payload,
      instanceUniqueId,
    },
    authConfig()
  );
  return response.data;
}

export async function completeCrmActivity(instanceUniqueId, activityId, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/activities/${activityId}/complete`,
    {
      ...payload,
      instanceUniqueId,
    },
    authConfig()
  );
  return response.data;
}

export async function transitionCrmOrder(instanceUniqueId, orderId, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/orders/${orderId}/transition`,
    {
      ...payload,
      instanceUniqueId,
      sendEmail: false,
    },
    authConfig()
  );
  return response.data;
}

export async function fetchCrmOrderDocumentOptions(instanceUniqueId, orderId, documentType) {
  const response = await axios.get(
    `${BASE_URL}/crm/orders/${orderId}/document-options`,
    authConfig({
      params: {
        instanceUniqueId,
        documentType,
      },
    })
  );
  return response.data;
}

export async function createCrmOrderCustomerAddress(instanceUniqueId, orderId, payload = {}) {
  const response = await axios.post(
    `${BASE_URL}/crm/orders/${orderId}/customer-addresses`,
    {
      ...payload,
      instanceUniqueId,
    },
    authConfig()
  );
  return response.data;
}

export async function previewCrmOrderTransition(instanceUniqueId, orderId, documentType) {
  const response = await axios.get(
    `${BASE_URL}/crm/orders/${orderId}/transition-preview`,
    authConfig({
      params: {
        instanceUniqueId,
        documentType,
      },
    })
  );
  return response.data;
}

export async function claimProspect(instanceUniqueId, prospectId) {
  return axios.post(
    `${BASE_URL}/crm/prospects/${prospectId}/claim`,
    { instanceUniqueId },
    authConfig()
  );
}

export async function assignProspect(instanceUniqueId, prospectId, payload) {
  return axios.post(
    `${BASE_URL}/crm/prospects/${prospectId}/assign`,
    {
      ...payload,
      instanceUniqueId,
    },
    authConfig()
  );
}

export async function reassignProspect(instanceUniqueId, prospectId, payload) {
  return axios.post(
    `${BASE_URL}/crm/prospects/${prospectId}/reassign`,
    {
      ...payload,
      instanceUniqueId,
    },
    authConfig()
  );
}

export async function unassignProspect(instanceUniqueId, prospectId, payload = {}) {
  return axios.post(
    `${BASE_URL}/crm/prospects/${prospectId}/unassign`,
    {
      ...payload,
      instanceUniqueId,
    },
    authConfig()
  );
}

export async function fetchProspectAssignmentHistory(instanceUniqueId, prospectId) {
  return axios.get(
    `${BASE_URL}/crm/prospects/${prospectId}/assignment-history`,
    authConfig(withInstanceUniqueId(instanceUniqueId))
  );
}
